package ai.jessie.web.pages.organisations

import ai.jessie.web.onboarding.LocalOnboardingPort
import ai.jessie.web.onboarding.LocalOrganisationSelectionPort
import ai.jessie.web.onboarding.OrganisationStartState
import ai.jessie.web.onboarding.OrganisationSwitchState
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.organisationRequiredProblem
import ai.jessie.web.onboarding.organisationStart
import ai.jessie.web.onboarding.organisationSwitch
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.TextCardStyle
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.homeHref
import ai.jessie.web.theme.homeSideItems
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div

private const val ORGANISATION_TITLE = "Deine Organisation"

@Page("{organisationId}")
@Composable
fun organisationPage(ctx: PageContext) {
    val organisationId = ctx.route.dynamicParams["organisationId"].orEmpty()
    if (!validOrganisationId(organisationId)) {
        // UC-006 A1: no organisation is identified, so no request is sent and no data is shown.
        appFrame(
            organisationId,
            "/organisations/$organisationId",
            ORGANISATION_TITLE,
            side = homeSideItems(organisationId),
        ) {
            Div(attrs = AlertStyle.toAttrs()) {
                onboardingProblem(organisationRequiredProblem(), busy = false)
            }
        }
    } else key(organisationId, ctx.route.params["home"]) { organisationContent(organisationId) }
}

@Composable
private fun organisationContent(organisationId: String) {
    val port = LocalOnboardingPort.current
    val scope = rememberCoroutineScope()
    val selectionPort = LocalOrganisationSelectionPort.current
    val ctx = rememberPageContext()
    val model =
        remember(port, scope, organisationId) {
            OrganisationStartState(port, scope, organisationId)
        }
    // No organisation-scoped editors exist yet; they supply unsaved-change detection here.
    val switch =
        remember(selectionPort, port, scope, organisationId) {
            OrganisationSwitchState(selectionPort, port, scope, organisationId)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    LaunchedEffect(model.state.data, ctx.route.params["home"]) {
        model.state.data?.let {
            if (
                ctx.route.params["home"] == "true" ||
                    (ctx.route.params["home"] == "onboarding" && it.role == "ACCOUNTANT")
            )
                ctx.router.navigateTo(homeHref(organisationId, it.role), UpdateHistoryMode.REPLACE)
        }
    }
    LaunchedEffect(switch.state.selectedOrganisationId) {
        switch.state.selectedOrganisationId?.let { ctx.router.navigateTo("/organisations/$it") }
    }
    val authenticationRequired =
        model.state.error?.authenticationRequired == true ||
            switch.state.error?.authenticationRequired == true
    // Part of the "Home" area; the start data and the switch share one card.
    appFrame(
        organisationId,
        "/organisations/$organisationId",
        ORGANISATION_TITLE,
        authenticationRequired = authenticationRequired,
        side = homeSideItems(organisationId),
    ) {
        Div(attrs = listOf(CardStyle, TextCardStyle).toAttrs()) {
            Div(attrs = CardBodyStyle.toAttrs()) {
                // A committed or revoked organisation hides current data until the page is
                // replaced.
                if (switch.state.selectedOrganisationId == null && !switch.state.activeRevoked)
                    organisationStart(model.state, model::refresh)
                if (switch.state.selectedOrganisationId == null)
                    organisationSwitch(organisationId, switch.state, switch.actions)
            }
        }
    }
}
